/**
 * /prompts — Prompt Library (auth-gated).
 * =========================================
 * List your saved prompts, drill into version history, create new versions,
 * diff between any two versions, promote a version to production/staging.
 *
 * Brand: slate-950, emerald accents.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Library, Plus, Trash2, GitBranch, Tag, ArrowLeft, GitCompare,
  Sparkles, ArrowRight, FileText, History,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

// ─────── List view ───────
const PromptList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', system_message: 'You are a helpful assistant.', user_template: '', default_model: 'claude-sonnet-4-6' });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/prompts/`, authHeaders());
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
      else toast.error('Failed to load prompts');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onCreate = async () => {
    if (!form.name || !form.user_template) { toast.error('Name + user template are required.'); return; }
    try {
      const { data } = await axios.post(`${API}/prompts/`, form, authHeaders());
      toast.success(`Created "${data.prompt.name}"`);
      setCreating(false);
      navigate(`/prompts/${data.prompt.prompt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this prompt? (soft delete, recoverable from DB)')) return;
    await axios.delete(`${API}/prompts/${id}`, authHeaders()).catch(() => {});
    load();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="prompts-list-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex items-center gap-3">
            <Link to="/prompt-playground" className="text-sm text-slate-300 hover:text-white">Open Playground</Link>
            <Link to="/traces" className="text-sm text-slate-300 hover:text-white">Traces</Link>
            <Link to="/evals" className="text-sm text-slate-300 hover:text-white">Evals</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
              <Library className="w-3 h-3 mr-1.5 inline" /> Prompt library
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="prompts-h1"><BrandTitle>Prompt Library</BrandTitle></h1>
            <p className="text-slate-300 mt-1">Versioned, labelable, production-safe.</p>
          </div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="prompts-create-btn">
                <Plus className="w-4 h-4 mr-2" /> New prompt
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">Create new prompt</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-slate-950 border-slate-800" placeholder="e.g. checkout-intent-classifier" data-testid="prompts-create-name" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Description</label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-slate-950 border-slate-800" placeholder="Optional — what does this prompt do?" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">System message</label>
                  <Textarea value={form.system_message} onChange={(e) => setForm({ ...form, system_message: e.target.value })} className="mt-1 bg-slate-950 border-slate-800 min-h-[70px] font-mono text-sm" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">User template</label>
                  <Textarea value={form.user_template} onChange={(e) => setForm({ ...form, user_template: e.target.value })} className="mt-1 bg-slate-950 border-slate-800 min-h-[120px] font-mono text-sm" placeholder={'Hello {{name}}, your order total is {{total}}.'} data-testid="prompts-create-template" />
                  <p className="text-[10px] text-slate-500 mt-1">Use <code className="text-emerald-300">{'{var}'}</code> or <code className="text-emerald-300">{'{{var}}'}</code> placeholders.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={onCreate} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="prompts-create-submit">Create v1</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="bg-slate-900/60 border-white/10 border-dashed shadow-sm">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-200 font-medium">No saved prompts yet.</p>
              <p className="text-slate-500 text-sm mt-1">Try the <Link to="/prompt-playground" className="text-emerald-600 hover:underline">Playground</Link>, then save what works.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((p) => (
              <Card key={p.prompt_id} className="bg-slate-900/60 border-white/10 hover:border-emerald-300 hover:shadow-md shadow-sm transition-all group" data-testid={`prompts-card-${p.prompt_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <Link to={`/prompts/${p.prompt_id}`} className="flex-1">
                      <h3 className="font-bold text-white group-hover:text-emerald-300 truncate">{p.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description || 'No description'}</p>
                    </Link>
                    <button onClick={() => onDelete(p.prompt_id)} className="text-slate-400 hover:text-rose-500 ml-3" data-testid={`prompts-delete-${p.prompt_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-[10px]">
                    <span className="flex items-center gap-1 text-slate-500"><History className="w-3 h-3" /> v{p.latest_version_number}</span>
                    {p.labels?.production && <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-1.5 py-0 text-[9px]">prod=v{p.labels.production}</Badge>}
                    {p.labels?.staging && <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-1.5 py-0 text-[9px]">staging=v{p.labels.staging}</Badge>}
                    <span className="text-slate-400 ml-auto">{fmtDate(p.updated_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ─────── Detail view ───────
const PromptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diff, setDiff] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [newVer, setNewVer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/prompts/${id}`, authHeaders());
      setPrompt(data.prompt);
      setVersions(data.versions || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
      else toast.error('Failed to load prompt');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const onPromote = async (versionNumber, label) => {
    try {
      await axios.post(`${API}/prompts/${id}/promote`, { version_number: versionNumber, label }, authHeaders());
      toast.success(`v${versionNumber} → ${label}`);
      load();
    } catch (e) {
      toast.error('Promote failed');
    }
  };

  const onNewVersion = async () => {
    if (!newVer?.user_template) { toast.error('Template required'); return; }
    try {
      const { data } = await axios.post(`${API}/prompts/${id}/versions`, newVer, authHeaders());
      toast.success(`Created v${data.version_number}`);
      setNewVer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const openDiff = async (a, b) => {
    try {
      const { data } = await axios.get(`${API}/prompts/${id}/diff?from=${a}&to=${b}`, authHeaders());
      setDiff(data);
      setDiffOpen(true);
    } catch { toast.error('Diff failed'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 text-slate-100 text-slate-300 p-10">Loading…</div>;
  if (!prompt) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="prompts-detail-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/prompts" className="flex items-center gap-2 text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Library</Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">{prompt.name}</h1>
            <p className="text-slate-300 text-sm mt-1">{prompt.description || 'No description'}</p>
            <div className="flex items-center gap-2 mt-3">
              {prompt.labels?.production && <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-2 py-0.5"><Tag className="w-3 h-3 mr-1 inline" /> production = v{prompt.labels.production}</Badge>}
              {prompt.labels?.staging && <Badge className="bg-amber-500/15 border-amber-300 text-amber-300 px-2 py-0.5"><Tag className="w-3 h-3 mr-1 inline" /> staging = v{prompt.labels.staging}</Badge>}
            </div>
          </div>
          <Button
            onClick={() => setNewVer({
              system_message: versions[0]?.system_message || '',
              user_template: versions[0]?.user_template || '',
              default_model: versions[0]?.default_model || 'claude-sonnet-4-6',
              commit_message: '',
            })}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            data-testid="prompts-new-version-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New version
          </Button>
        </div>

        <div className="space-y-3" data-testid="prompts-versions-list">
          {versions.map((v) => (
            <Card key={v.version_number} className="bg-slate-900/60 border-white/10 shadow-sm" data-testid={`prompts-version-${v.version_number}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-slate-900/60 border-white/10 text-slate-200 px-2 py-0.5 font-mono">v{v.version_number}</Badge>
                    <span className="text-xs text-slate-500">{fmtDate(v.created_at)}</span>
                    <span className="text-xs text-slate-500">· model: {v.default_model}</span>
                    {prompt.labels?.production === v.version_number && <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-1.5 py-0 text-[10px]">production</Badge>}
                    {prompt.labels?.staging === v.version_number && <Badge className="bg-amber-500/15 border-amber-300 text-amber-300 px-1.5 py-0 text-[10px]">staging</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-white/15 text-slate-200 hover:bg-slate-900/60" onClick={() => onPromote(v.version_number, 'staging')} data-testid={`promote-staging-${v.version_number}`}>→ staging</Button>
                    <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-300 hover:bg-emerald-500/10" onClick={() => onPromote(v.version_number, 'production')} data-testid={`promote-prod-${v.version_number}`}>→ production</Button>
                    {versions.length > 1 && v.version_number !== Math.max(...versions.map((x) => x.version_number)) && (
                      <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-900/60" onClick={() => openDiff(v.version_number, Math.max(...versions.map((x) => x.version_number)))} data-testid={`diff-${v.version_number}`}>
                        <GitCompare className="w-3.5 h-3.5 mr-1" /> diff vs latest
                      </Button>
                    )}
                  </div>
                </div>
                {v.commit_message && <p className="text-xs text-slate-300 mt-2 italic">"{v.commit_message}"</p>}
                <details className="mt-3">
                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-200 select-none">Show template</summary>
                  <pre className="mt-2 p-3 bg-slate-900/60 rounded text-xs text-slate-100 font-mono whitespace-pre-wrap border border-white/10">{v.user_template}</pre>
                </details>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={!!newVer} onOpenChange={(o) => !o && setNewVer(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
            <DialogHeader><DialogTitle>New version (v{Math.max(...versions.map((v) => v.version_number)) + 1})</DialogTitle></DialogHeader>
            {newVer && (
              <div className="space-y-3">
                <Textarea value={newVer.system_message} onChange={(e) => setNewVer({ ...newVer, system_message: e.target.value })} className="bg-slate-950 border-slate-800 min-h-[60px] font-mono text-sm" placeholder="System message" />
                <Textarea value={newVer.user_template} onChange={(e) => setNewVer({ ...newVer, user_template: e.target.value })} className="bg-slate-950 border-slate-800 min-h-[140px] font-mono text-sm" placeholder="User template" />
                <Input value={newVer.commit_message} onChange={(e) => setNewVer({ ...newVer, commit_message: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Commit message — what changed?" data-testid="new-version-commit" />
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNewVer(null)}>Cancel</Button>
              <Button onClick={onNewVersion} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="new-version-submit">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl">
            <DialogHeader><DialogTitle>Diff · v{diff?.from?.version_number} → v{diff?.to?.version_number}</DialogTitle></DialogHeader>
            {diff && (
              <pre className="text-xs bg-slate-950 p-4 rounded border border-slate-800 overflow-auto max-h-[60vh] whitespace-pre-wrap" data-testid="prompts-diff-view">
                {(diff.template_diff || []).map((line, i) => (
                  <span key={i} className={line.startsWith('+') ? 'text-emerald-300 block' : line.startsWith('-') ? 'text-rose-300 block' : 'text-slate-400 block'}>{line}</span>
                ))}
              </pre>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

const PromptLibrary = () => {
  const { id } = useParams();
  return id ? <PromptDetail /> : <PromptList />;
};

export default PromptLibrary;
